/* The Summary: every report for one year and month side by side, each field summed over the report's locations (sites),
 * plus an "All reports" column. Built from the same layout rows the report view uses, so labels and order match. */
import S from './core.js';
import { statusOf } from './approvals.js';
import { MONTH_NAMES, periodOf } from './periods.js';

const BY_SITE_LABELS = { total_power_usage_kwh_by_site: 'Total Power Usage (kWh)', power_revenue_gbp_by_site: 'Power Revenue (£)' };
const SECTION_TITLES = { site_detail: 'Site, chargepoint & socket detail', financials: 'Financials (£)', by_site: 'By site - summed over all sites' };

/** Keep the latest as-at version of each report id for the year. */
export function latestPerReport(list, year) {
  const best = new Map();
  list.filter((r) => r.report_year === year).forEach((r) => {
    const cur = best.get(r.settlement_report_id);
    if (!cur || r.as_at_date > cur.as_at_date) best.set(r.settlement_report_id, r);
  });
  return [...best.values()].sort((a, b) => a.settlement_report_id.localeCompare(b.settlement_report_id));
}

/** entries: [{ report, figures }]; approvals: report_approval rows. */
export function buildSummary(entries, year, monthIdx, approvals) {
  const period = periodOf({ mode: 'monthly', year, month: monthIdx });
  const master = [];                                   // ordered row keys across every report
  const meta = new Map();                              // key -> { section, label, kind }
  const columns = entries.map(({ report, figures }) => {
    const values = new Map();
    const order = [];
    S.buildLayout(report, figures).pages.forEach((pg) => pg.rows.forEach((row) => {
      if (row.k !== 'grid') return;
      const key = `${row.section}|${row.code}`;
      const cell = row.cells[monthIdx];
      const v = cell && cell.v !== null ? cell.v : null;
      if (!values.has(key)) { values.set(key, null); order.push(key); }
      if (v !== null) values.set(key, (values.get(key) || 0) + v);          // by_site rows: one per site, so this sums the sites
      if (!meta.has(key)) {
        meta.set(key, { key, section: row.section, kind: row.kind,
          label: row.section === 'by_site' ? `${BY_SITE_LABELS[row.code] || row.code.replace(/_/g, ' ')} - all sites` : row.label });
      }
    }));
    let at = -1;                                       // merge this report's row order into the master order
    order.forEach((key) => {
      let i = master.indexOf(key);
      if (i < 0) { i = at + 1; master.splice(i, 0, key); }
      at = i;
    });
    const status = statusOf(approvals, report, period);
    return { key: `${report.settlement_report_id}/${report.report_year}/${report.as_at_date}`, id: report.settlement_report_id, client: report.client_name || '',
      manager: report.account_manager || '', values, finance: status.finance, account_manager: status.account_manager };
  });

  const sections = [];
  master.forEach((key) => {
    const m = meta.get(key);
    let sec = sections.find((s) => s.section === m.section);
    if (!sec) { sec = { section: m.section, title: SECTION_TITLES[m.section] || m.section, rows: [] }; sections.push(sec); }
    const values = columns.map((c) => (c.values.has(key) ? c.values.get(key) : null));
    const present = values.filter((v) => v !== null);
    const additive = m.kind !== 'pct';                 // percentages are not added up
    sec.rows.push({ key, label: m.label, kind: m.kind, values, total: additive && present.length ? present.reduce((a, b) => a + b, 0) : null });
  });
  return { year, monthIdx, month: MONTH_NAMES[monthIdx], title: `Summary - ${MONTH_NAMES[monthIdx]} ${year}`, columns, sections };
}

export const fmt = (kind, v) => (v === null || v === undefined ? '' : S.fmtNum(kind, kind === 'money' ? Math.round((v + Number.EPSILON) * 100) / 100 : v));
